import React, { useState } from 'react'
import { Button, Card, Empty, Flex, Popconfirm, Table, Tag, Typography } from 'antd'
import { useIntegrationTokens, useRevokeIntegrationToken } from '../hooks'
import { IntegrationToken } from '../integration-token'
import { GenerateModal } from './generate-modal'
import { t } from 'i18next'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { TimeAgo } from '../../components/time-ago'
import { DESTROY_INTEGRATION_TOKEN, STORE_INTEGRATION_TOKEN } from '../../user/permission'
import { useAuth } from '../../auth'

export function IntegrationTokensList() {
    const [showGenerateModal, setShowGenerateModal] = useState(false)
    const { data: tokens, isLoading } = useIntegrationTokens()
    const { can } = useAuth()
    const { mutate: revokeToken, isPending: isRevoking } = useRevokeIntegrationToken()

    const handleRevokeToken = (tokenId: string) => {
        revokeToken(tokenId)
    }

    const columns = [
        {
            title: t('Status'),
            key: 'status',
            width: 100,
            render: (token: IntegrationToken) => (
                <Tag color={token.isExpired ? 'red' : 'green'}>{token.isExpired ? t('Expired') : t('Active')}</Tag>
            ),
        },
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('Last Used'),
            dataIndex: 'lastUsedAt',
            key: 'lastUsedAt',
            width: 180,
            render: (lastUsedAt: string | null) => (lastUsedAt ? <TimeAgo date={new Date(lastUsedAt)} /> : t('Never')),
        },
        {
            title: t('Created'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (createdAt: string) => <TimeAgo date={new Date(createdAt)} />,
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: 120,

            render: (token: IntegrationToken) => (
                <Popconfirm
                    title={t('Are you sure you want to revoke this token?')}
                    description={t('Applications using this token will lose access.')}
                    onConfirm={() => handleRevokeToken(token.id)}
                    okText={t('Yes, revoke')}
                    cancelText={t('Cancel')}
                    okButtonProps={{ danger: true }}
                >
                    <Button
                        disabled={!can(DESTROY_INTEGRATION_TOKEN)}
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={isRevoking}
                    >
                        {t('Revoke')}
                    </Button>
                </Popconfirm>
            ),
        },
    ]

    return (
        <Card
            title={t('Integration Tokens')}
            extra={
                <Button
                    type="primary"
                    disabled={!can(STORE_INTEGRATION_TOKEN)}
                    icon={<PlusOutlined />}
                    onClick={() => setShowGenerateModal(true)}
                >
                    {t('Generate Token')}
                </Button>
            }
        >
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t(
                        'Integration tokens allow external applications to authenticate with your company account via the API.'
                    )}
                </Typography.Text>

                {tokens && tokens.length > 0 ? (
                    <Table
                        scroll={{
                            x: 800,
                        }}
                        dataSource={tokens}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={false}
                        size="small"
                    />
                ) : (
                    <Empty
                        description={t('No integration tokens found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Flex>

            <GenerateModal
                open={showGenerateModal}
                onCancel={() => setShowGenerateModal(false)}
            />
        </Card>
    )
}
